import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Chip,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { NotificationManager } from "react-notifications";
import {
  GetDropdownListItems,
  DeleteDropdownListItem,
  getDropdownListErrorMessage,
  DROPDOWN_LISTS,
} from "../../../services/DropdownListService";
import CommonLoader from "../../common/CommonLoader";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import AddEditDropdownItemDialog from "./AddEditDropdownItemDialog";
import ReorderDropdownItemsDialog from "./ReorderDropdownItemsDialog";

// Add / edit / soft-delete the values of the admin-managed dropdown lists.
// One screen for every list: the user picks which list to manage (no "All" option on purpose,
// so it is always clear which dropdown is being changed).
const DropdownListMaintenance = () => {
  const [selectedList, setSelectedList] = useState(DROPDOWN_LISTS[0]);
  const { type: listType, label: title, itemLabel, maxLength } = selectedList;

  const columns = ["#", itemLabel, "Status", "Actions"];
  const rowsPerPageOptions = [10, 25, 50, 100];

  const [items, setItems] = useState([]);
  // Latest selected list, so a slow response for a previously selected list is ignored.
  const currentListType = useRef(listType);
  currentListType.current = listType;
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const movableCount = items.filter((item) => !item.isSystem).length;

  useEffect(() => {
    setSearchTerm("");
    setPage(0);
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listType]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    setFilteredData(items.filter((item) => item.value?.toLowerCase().includes(term)));
    setPage(0);
  }, [searchTerm, items]);

  const fetchItems = async () => {
    const requestedType = listType;
    try {
      setItems([]);
      setLoading(true);
      // Admin view shows inactive items too, so they can be re-activated.
      const response = await GetDropdownListItems(requestedType, true);
      if (currentListType.current === requestedType) setItems(response.results || []);
    } catch (error) {
      console.error("Error fetching dropdown list:", error);
      NotificationManager.error(`Error loading ${title}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditItem(null);
    setOpenDialog(true);
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setOpenDialog(true);
  };

  const handleDeleteClick = (item) => {
    setSelectedItemToDelete(item);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteDropdownListItem(selectedItemToDelete.id);
      setIsDeleteConfirmationOpen(false);
      setItems(items.filter((item) => item.id !== selectedItemToDelete.id));
      NotificationManager.success(`${itemLabel} deleted successfully`);
    } catch (error) {
      setIsDeleteConfirmationOpen(false);
      console.error("Error deleting dropdown item:", error);
      NotificationManager.error(await getDropdownListErrorMessage(error, `Error deleting ${itemLabel.toLowerCase()}`));
    }
  };

  const renderActions = (item) =>
    item.isSystem ? (
      // 'Other' must always exist so users have a fallback choice.
      <Tooltip title="System item: always available, cannot be edited or deleted">
        <LockIcon color="disabled" sx={{ ml: 1 }} />
      </Tooltip>
    ) : (
      <div style={{ minWidth: "90px" }}>
        <IconButton aria-label="edit" style={{ color: "orange" }} onClick={() => handleEditClick(item)}>
          <EditIcon />
        </IconButton>
        <IconButton aria-label="delete" style={{ color: "red" }} onClick={() => handleDeleteClick(item)}>
          <DeleteIcon />
        </IconButton>
      </div>
    );

  return (
    <>
      <span className="title">{title}</span>
      <hr />
      <div style={{ display: "flex", marginBottom: "16px", alignItems: "center" }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: "16px", flex: 1 }}
        />
        <Autocomplete
          options={DROPDOWN_LISTS}
          value={selectedList}
          onChange={(e, list) => list && setSelectedList(list)}
          getOptionLabel={(list) => list.label}
          isOptionEqualToValue={(option, value) => option.type === value.type}
          disableClearable
          size="small"
          sx={{ width: 320, marginRight: "16px" }}
          renderInput={(params) => <TextField {...params} label="Dropdown List" />}
        />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<SwapVertIcon />}
          onClick={() => setReorderDialogOpen(true)}
          disabled={movableCount < 2}
          title="Click here to re-order the items in this list"
          sx={{ whiteSpace: "nowrap", flexShrink: 0, marginRight: "16px" }}
        >
          Re-order Items
        </Button>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAdd}>
          New
        </Button>
      </div>
      <Paper style={{ marginRight: "20px" }}>
        <TableContainer>
          <Table sx={{ minWidth: 400 }} size="small">
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.primary.main, color: "white" }}>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={{ backgroundColor: index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white" }}
                  >
                    <TableCell sx={{ width: 60 }}>{item.isSystem ? "-" : item.sortOrder}</TableCell>
                    <TableCell>{item.value}</TableCell>
                    <TableCell sx={{ width: 110 }}>
                      <Chip
                        size="small"
                        label={item.isActive ? "Active" : "Inactive"}
                        color={item.isActive ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ width: 120 }}>{renderActions(item)}</TableCell>
                  </TableRow>
                ))}
              {!loading && filteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    No items found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
      <CommonLoader loading={loading} />

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete "${selectedItemToDelete?.value}"? Existing records that use it will keep their value.`}
        color="red"
      />
      <AddEditDropdownItemDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={fetchItems}
        selectedItem={editItem}
        listType={listType}
        itemLabel={itemLabel}
        maxLength={maxLength}
      />
      <ReorderDropdownItemsDialog
        open={reorderDialogOpen}
        onClose={() => setReorderDialogOpen(false)}
        listType={listType}
        title={title}
        items={items}
        onReorderComplete={fetchItems}
      />
    </>
  );
};

export default DropdownListMaintenance;
